import { listPointParts } from '@dealstack/shared/lib/common/business/documentFlow';
import type { FlowPredicate, FlowRules } from '@dealstack/shared/lib/common/business/documentFlow';
import type { DataPointWithSampleRes } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.types.ts';

import {
	dataPointChoices,
	toDataPointChoiceSource,
} from '@dealstack/shared/lib/documents/dataPointChoices';
import type { DataPointChoiceSource } from '@dealstack/shared/lib/documents/dataPointChoices';

import type { RuleCohort } from './rules.types';

/** A stored value shown as a person picked it — "US" reads back as "United States". */
const choiceLabel = (source: DataPointChoiceSource, value: string): string =>
	dataPointChoices(source).find((choice) => choice.value === value)?.label ?? value;

/** Declarations are the only genuine booleans in the catalogue, so they are the yes/no set. */
export const DECLARATION_PATH_PREFIX = 'data.declarations.';

const pointLabel = (path: string, dataPoints: DataPointWithSampleRes[]): string =>
	dataPoints.find((point) => point.path === path)?.label ?? path;

/** The test as a sentence, which is what the panel and the modal both show back. */
export const describePredicate = (
	predicate: FlowPredicate,
	dataPoints: DataPointWithSampleRes[],
	cohorts: RuleCohort[]
): string => {
	if (predicate.operator === 'inCohort') {
		const name =
			cohorts.find((cohort) => cohort.id === predicate.cohortId)?.name ??
			predicate.cohortName ??
			'a cohort';
		return `the investor is in “${name}”`;
	}
	const label = pointLabel(predicate.dataPointPath, dataPoints);
	// A point read off a list is tested against every entry, so the sentence says so.
	const isList = listPointParts(predicate.dataPointPath) !== null;
	if (predicate.operator === 'isOneOf' || predicate.operator === 'isNoneOf') {
		const source = toDataPointChoiceSource(
			dataPoints.find((point) => point.path === predicate.dataPointPath)?.choices
		);
		const named = predicate.values.map((value) =>
			source === undefined ? value : choiceLabel(source, value)
		);
		const joined =
			named.length === 1
				? named[0]
				: `${named.slice(0, -1).join(', ')} or ${named[named.length - 1]}`;
		if (isList) {
			return `${predicate.operator === 'isNoneOf' ? 'no' : 'any'} ${label} is ${joined ?? ''}`;
		}
		return `${label} is ${predicate.operator === 'isNoneOf' ? 'not ' : ''}${joined ?? ''}`;
	}
	if (predicate.operator === 'isBlank') {
		return isList ? `no ${label} is filled in` : `${label} is blank`;
	}
	if (predicate.operator === 'isNotBlank') {
		return isList ? `any ${label} is filled in` : `${label} is filled in`;
	}
	return `${label} is ${predicate.operator === 'isYes' ? 'Yes' : 'No'}`;
};

/** What the label in the page reads for one rule. */
export const ruleLabels = (rules: FlowRules): Record<string, string> =>
	Object.fromEntries(
		Object.entries(rules).map(([ruleId, rule]) => [ruleId, `Only if ${rule.label}`])
	);
